/* eslint-disable @typescript-eslint/naming-convention */
import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	HostListener,
	OnDestroy,
	OnInit,
	signal,
	ViewChild,
	ElementRef,
	AfterViewInit,
	Inject
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { MarkdownModule, MARKED_OPTIONS, MarkedOptions, MarkedRenderer, provideMarkdown } from 'ngx-markdown';
import { Subject } from 'rxjs';

import {
	ConversationManagementApiService,
	LangGraphConversationRequest,
	WorkflowApiService,
	StatePersistenceApiService
} from '@shure/cloud/shared/chat-assistant/data-access';
import { BreakpointService } from '@shure/cloud/shared/services/media-breakpoints';
import {
	PrismIconComponent,
	PrismButtonComponent,
	PrismFloatingDialogComponent,
	PrismInputComponent,
	PrismIconButtonComponent,
	TextValueAccessor,
	PrismRadioGroupComponent,
	PrismRadioButtonComponent,
	PrismDialogRef
} from '@shure/prism-angular-components';
import { ILogger } from '@shure/shared/angular/utils/logging';

import { ChatResponseTypeEnum, MessageList, MessageRoleEnum } from './models/chat-assistant.model';

/**
 * Configures markdown to render links with prism class.
 * @returns MarkedOptions configuration object
 */
function createPrismMarkedOptions(): MarkedOptions {
	const renderer = new MarkedRenderer();

	// Override link rendering to use Prism styles
	renderer.link = ({ href, title, text }): string => {
		const titleAttr = title ? ` title="${title}"` : '';
		return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer" class="prism-link">${text}</a>`;
	};

	renderer.image = ({ href, title, text }): string => {
		const titleAttr = title ? ` title="${title}"` : '';
		return `<a href="${href}" target="_blank" rel="noopener noreferrer">
                <img src="${href}" alt="${text}"${titleAttr}/> 
                </a>`;
	};

	return {
		renderer,
		gfm: true,
		breaks: true
	};
}
@Component({
	selector: 'sh-chat-assistant-main',
	standalone: true,
	imports: [
		CommonModule,
		TranslocoModule,
		PrismIconComponent,
		PrismButtonComponent,
		PrismFloatingDialogComponent,
		PrismInputComponent,
		FormsModule,
		ReactiveFormsModule,
		TextValueAccessor,
		PrismIconButtonComponent,
		PrismRadioGroupComponent,
		PrismRadioButtonComponent,
		MarkdownModule
	],
	providers: [
		provideMarkdown(),
		{
			provide: MARKED_OPTIONS,
			useFactory: createPrismMarkedOptions
		}
	],
	templateUrl: './chat-assistant-main.component.html',
	styleUrl: './chat-assistant-main.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatAssistantMainComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild('chatComponent') public chatComponent!: HTMLPrismFloatingDialogElement;
	@ViewChild('messagesListDiv') public messagesListDiv!: ElementRef<HTMLDivElement>;
	public conversationId = '';
	public threadId = '';
	public selectedOption = signal('');
	public readonly destroy$ = new Subject<void>();
	public messagesList = signal<MessageList[]>([]);
	public displayErrorMessage = signal(false);
	public formGroup: FormGroup = new FormGroup({});
	public isResponseGenerating = signal(false);
	public dialogPosition = signal<{ x: number; y: number } | undefined>(undefined);
	public isLandscape = this.breakpointService.isLandscape;
	public isSmallDevice = this.breakpointService.isLteSmall;

	constructor(
		private formBuilder: FormBuilder,
		public chatAssistantCreateApiService: ConversationManagementApiService,
		public readonly dialogRef: PrismDialogRef<ChatAssistantMainComponent>,
		public chatAssistantContinueApiService: WorkflowApiService,
		public savingWorkflowStateApiService: StatePersistenceApiService,
		private readonly logger: ILogger,
		@Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
		private breakpointService: BreakpointService
	) {
		this.logger = logger.createScopedLogger('ChatAssistantMainComponent');
	}

	/**
	 * Used to submit when the user clicks the Enter key.
	 * @param event - The keyboard event triggered by the user
	 */
	@HostListener('window:keydown', ['$event'])
	public handleEnterKey(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			if (this.formGroup.get('chatInput')?.value) {
				this.submitMessage();
			}
		}
	}
	/**
	 * Scrolls the chat messages container to the bottom to show the latest message.
	 */
	public scrollToBottom(): void {
		setTimeout(() => {
			const chatContainer = this.messagesListDiv.nativeElement;
			if (chatContainer) {
				chatContainer.scrollTop = chatContainer.scrollHeight;
			}
		}, 50);
	}

	/**
	 * Sets up the reactive form group with a required chat input control.
	 */
	public ngOnInit(): void {
		this.formGroup = this.formBuilder.group({});
		this.formGroup.addControl('chatInput', new FormControl('', [Validators.required]));
	}

	public ngAfterViewInit(): void {
		if (!this.isSmallDevice()) {
			this.chatComponent.position = { x: 1400, y: 56 };
		}
		//We are adding a small delay to allow the dialog to position itself before making it visible
		requestAnimationFrame(() => {
			this.chatComponent.visible = true;
		});
	}

	/**
	 * Clears the chat messages list and resets the conversation ID.
	 * Used to start a fresh conversation or clear the chat history.
	 */
	public emptyMessageList(): void {
		this.messagesList.set([]);
		this.isResponseGenerating.set(false);
		this.displayErrorMessage.set(false);
		this.threadId = '';
		this.conversationId = '';
	}

	/**
	 * Submits a user message to the chat assistant.
	 * Creates a new conversation if one doesn't exist, adds the message to the list,
	 * and initiates the conversation flow with the assistant.
	 */
	public submitMessage(): void {
		const chatInput = this.formGroup.get('chatInput');
		const msgObject = {
			type: ChatResponseTypeEnum.FinalState,
			role: MessageRoleEnum.User,
			content: chatInput?.value || ''
		};
		this.messagesList.update((values) => [...values, msgObject]);
		this.displayErrorMessage.set(false);
		chatInput?.setValue('');
		this.isResponseGenerating.set(true);
		this.scrollToBottom();
		const lastMessage = this.messagesList()[this.messagesList().length - 1];
		const body = {
			messages: [
				{
					role: lastMessage?.role || MessageRoleEnum.User,
					content: lastMessage?.content || '',
					id: ''
				}
			],
			thread_id: this.threadId
		};
		this.continueConversation(body);
	}

	/**
	 * Submits a user-selected option as confirmation to the chat assistant.
	 * Used when the assistant presents options and the user makes a selection.
	 * Clears the selected option and continues the conversation with the confirmation.
	 */
	public submitSelectedOption(): void {
		this.isResponseGenerating.set(true);
		this.scrollToBottom();
		const lastMessage = this.messagesList()[this.messagesList().length - 2];
		const body = {
			messages: [
				{
					role: lastMessage?.role || MessageRoleEnum.User,
					content: lastMessage?.content || '',
					id: this.conversationId
				}
			],
			thread_id: this.threadId,
			user_input: { confirmed_product: this.selectedOption(), human_feedback: 'confirmed' }
		};
		this.selectedOption.set('');
		this.continueConversation(body);
	}

	/**
	 * Copies the assistant's response text to the user's clipboard.
	 * @param assistantResponse - The text content to copy to clipboard
	 */
	public copyResponseToClipboard(assistantResponse: string | undefined): void {
		if (assistantResponse) {
			navigator.clipboard.writeText(assistantResponse);
		}
	}

	/**
	 * Continues an existing conversation by sending a request to the LangGraph API.
	 * Handles the streaming response and manages error states.
	 * @param body - The conversation request containing messages and thread information
	 */
	public async continueConversation(body: LangGraphConversationRequest): Promise<void> {
		try {
			const apiUrl = `${this.chatAssistantContinueApiService.rootUrl}/langgraph/conversation`;
			const accessToken = this.oktaAuth.getAccessToken();

			const response = await fetch(apiUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/x-ndjson',
					Authorization: `Bearer ${accessToken}`
				},
				body: JSON.stringify(body)
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			if (!response.body) {
				throw new Error('Response body is null');
			}

			const reader = response.body.getReader();
			const decoder = new TextDecoder('utf-8');
			let buffer = '';

			while (true) {
				const { value, done } = await reader.read();
				if (done) break;

				buffer += decoder.decode(value, { stream: true });
				const lines = buffer.split('\n');
				buffer = lines.pop() || '';

				for (const line of lines) {
					if (line.trim()) {
						try {
							const msg = JSON.parse(line);
							this.processStreamMessage(msg);
						} catch (parseError) {
							this.displayErrorMessage.set(true);
							this.logger.error('continueConversation', 'Error parsing JSON line', { parseError, line });
						}
					}
				}
			}
		} catch (error) {
			this.isResponseGenerating.set(false);
			this.displayErrorMessage.set(true);
			this.scrollToBottom();
			this.logger.error('continueConversation', 'Error fetching conversation', { error });
		}
	}

	/**
	 * Processes individual stream messages from the chat assistant API response.
	 * Handles different message types: status updates, final states, and waiting for input.
	 * Updates the UI state and saves workflow responses as needed.
	 * @param msg - The parsed JSON message from the stream
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public processStreamMessage(msg: any): void {
		if (msg.type === ChatResponseTypeEnum.Status) {
			this.threadId = msg.thread_id;
		}
		if (msg.type === ChatResponseTypeEnum.FinalState || msg.type === ChatResponseTypeEnum.WaitingForInput) {
			const formattedMsg = this.formatChatResponse(msg);
			this.messagesList.update((values) => [...values, formattedMsg]);
			this.isResponseGenerating.set(false);
			this.scrollToBottom();
		}
	}

	/**
	 * Formats chat response data into a standardized MessageList format.
	 * @param response - The raw response object from the API
	 * @returns A formatted MessageList object
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public formatChatResponse(response: any): MessageList {
		if (response.type === ChatResponseTypeEnum.FinalState) {
			return {
				type: ChatResponseTypeEnum.FinalState,
				role: MessageRoleEnum.Assistant,
				content: response.data?.workflow_state?.response_generation?.response || ''
			};
		}
		return {
			type: ChatResponseTypeEnum.WaitingForInput,
			role: MessageRoleEnum.Assistant,
			options: response?.input_request?.options || []
		};
	}

	/**
	 * Trims leading and trailing whitespace from the given content string.
	 * @param content - The input string to trim
	 * @returns The trimmed string
	 */
	public trimResponse(content: string | undefined): string {
		return content?.trim() || '';
	}

	public onDialogClose(): void {
		this.dialogRef.close();
	}

	/**
	 * Angular lifecycle hook called when the component is being destroyed.
	 * Completes the destroy subject to clean up subscriptions and prevent memory leaks.
	 */
	public ngOnDestroy(): void {
		this.destroy$.complete();
	}
}
